import React, { useState } from 'react';
import { OpinionSearchResult, SearchedOpinion } from '../types';
import { FileText, Scale, MapPin, User, Copy, ChevronDown, ChevronUp, Star, Bot } from 'lucide-react';

interface EnhancedSearchResultsListProps {
  searchResults: OpinionSearchResult;
}

export const EnhancedSearchResultsList: React.FC<EnhancedSearchResultsListProps> = ({ 
  searchResults
}) => {
  const [expandedOpinions, setExpandedOpinions] = useState<Set<string>>(new Set());
  const [expandedParagraphs, setExpandedParagraphs] = useState<Set<string>>(new Set());
  const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());

  // Use searchResults to map opinions
  const opinions = mapSearchResultsToOpinions(searchResults);

  if (!opinions || opinions.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg bg-gray-50 p-4">
        <div className="text-center text-gray-500">
          <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>No search results found</p>
        </div>
      </div>
    );
  }

  const toggleExpanded = (opinionId: string) => {
    const newExpanded = new Set(expandedOpinions);
    if (newExpanded.has(opinionId)) {
      newExpanded.delete(opinionId);
    } else {
      newExpanded.add(opinionId);
    }
    setExpandedOpinions(newExpanded);
  };

  const toggleParagraph = (paragraphId: string) => {
    const newExpanded = new Set(expandedParagraphs);
    if (newExpanded.has(paragraphId)) {
      newExpanded.delete(paragraphId);
    } else {
      newExpanded.add(paragraphId);
    }
    setExpandedParagraphs(newExpanded);
  };

  const copyToClipboard = async (text: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems(prev => new Set([...prev, itemId]));
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const formatScore = (score: number) => {
    return (score * 100).toFixed(1);
  };

  return (
    <div data-component="EnhancedSearchResultsList" className="border border-blue-200 rounded-lg bg-blue-50 p-4">
      <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
        <Scale className="w-4 h-4" />
        Search Results ({opinions.length} found)
      </h4>
      
      <div className="space-y-4">
        {opinions.map((opinion) => (
          <div key={opinion.id} className="bg-white border border-blue-200 rounded-lg p-4">
            {/* Case Header */}
            <div className="mb-3">
              {/* AI Summary Badge */}
              {opinion.aiSummary && opinion.aiSummary.trim() !== '' && (
                <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">
                      <Bot className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-purple-700 mb-1">✨ AI Summary</div>
                      <div className="text-sm text-gray-800 leading-relaxed">{opinion.aiSummary}</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  {opinion.caseNumber && (
                    <div className="text-sm font-medium text-blue-800 mb-1">
                      Case #{opinion.caseNumber}
                    </div>
                  )}
                  
                  {/* Overall Score */}
                  {opinion.score && (
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-yellow-500" />
                        <span className="text-xs text-gray-600">
                          Relevance: {formatScore(opinion.score)}%
                        </span>
                      </div>
                      
                      {/* Matched Query Parts */}
                      {opinion.matchedQueryPartIndices && opinion.matchedQueryPartIndices.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500">Matched:</span>
                          <div className="flex gap-1">
                            {opinion.matchedQueryPartIndices.map(idx => (
                              <span 
                                key={idx}
                                className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-green-100 text-green-700 rounded-full"
                              >
                                {idx + 1}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => toggleExpanded(opinion.id)}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                >
                  {expandedOpinions.has(opinion.id) ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Collapse
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Expand Details
                    </>
                  )}
                </button>
              </div>
              
              {/* Case Title */}
              {opinion.title && (
                <div className="text-base font-semibold text-gray-900 mb-3 leading-snug">
                  {opinion.title}
                </div>
              )}

              {/* Expanded Details */}
              {expandedOpinions.has(opinion.id) && (
                <div className="mt-3 p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
                  {/* Row 1: Court | State */}
                  <div className="grid grid-cols-2 gap-4">
                    {opinion.court && (
                      <div className="flex items-start gap-2">
                        <Scale className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Court:</span> {opinion.court}
                        </div>
                      </div>
                    )}
                    
                    {opinion.state && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">State:</span> {opinion.state}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Row 2: Plaintiffs | Defendants */}
                  <div className="grid grid-cols-2 gap-4">
                    {opinion.plaintiffs.length > 0 && (
                      <div className="flex items-start gap-2">
                        <User className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Plaintiffs:</span> {opinion.plaintiffs.join(', ')}
                        </div>
                      </div>
                    )}
                    
                    {opinion.defendants.length > 0 && (
                      <div className="flex items-start gap-2">
                        <User className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Defendants:</span> {opinion.defendants.join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Row 3: Judge | Filed At */}
                  <div className="grid grid-cols-2 gap-4">
                    {opinion.judge && (
                      <div className="flex items-start gap-2">
                        <User className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Judge:</span> {opinion.judge}
                        </div>
                      </div>
                    )}
                    
                    {opinion.fillingDate && (
                      <div className="flex items-start gap-2">
                        <FileText className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Filed At:</span> {opinion.fillingDate}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Summary */}
                  {opinion.summary && (
                    <div className="mt-3">
                      <div className="font-medium mb-1">Summary:</div>
                      <div className="text-gray-700 leading-relaxed">
                        {opinion.summary}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Relevant Paragraphs */}
            {opinion.paragraphs && opinion.paragraphs.length > 0 && (
              <div className="space-y-3">
                <h5 className="font-medium text-gray-800 text-sm">
                  Relevant Passages ({opinion.paragraphs.length})
                </h5>
                
                {opinion.paragraphs.map((paragraph, index) => {
                  const paragraphId = `${opinion.id}-paragraph-${index}`;
                  const isExpanded = expandedParagraphs.has(paragraphId);
                  const textPreview = paragraph.text.length > 200 
                    ? paragraph.text.substring(0, 200) + '...' 
                    : paragraph.text;
                  const showToggle = paragraph.text.length > 200;

                  return (
                    <div key={paragraphId} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      {/* AI Summary for Paragraph */}
                      {paragraph.aiSummary && paragraph.aiSummary.trim() !== '' && (
                        <div className="mb-3 p-2 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded">
                          <div className="flex items-start gap-2">
                            <div className="flex-shrink-0 mt-0.5">
                              <Bot className="w-3 h-3 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <div className="text-xs font-semibold text-purple-700 mb-0.5">✨ AI Key Point</div>
                              <div className="text-xs text-gray-800 leading-relaxed">{paragraph.aiSummary}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <Star className="w-3 h-3 text-yellow-500" />
                          <span>Score: {formatScore(paragraph.score)}%</span>
                          <span>•</span>
                          <span>Position: {paragraph.offset}-{paragraph.offset + paragraph.length}</span>
                          <span>•</span>
                          <span>Length: {paragraph.length} chars</span>
                        </div>
                        
                        <button
                          onClick={() => copyToClipboard(paragraph.text, paragraphId)}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs"
                        >
                          <Copy className="w-3 h-3" />
                          {copiedItems.has(paragraphId) ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      
                      <div className="text-sm text-gray-800 leading-relaxed">
                        {renderHighlightedText(isExpanded ? paragraph.text : textPreview, paragraph.highlights)}
                      </div>

                      {showToggle && (
                        <button
                          onClick={() => toggleParagraph(paragraphId)}
                          className="mt-2 flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="w-3 h-3" />
                              Show Less
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-3 h-3" />
                              Show More
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Copy Opinion ID */}
            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="text-xs text-gray-500">
                  Opinion ID: {opinion.id}
                </div>
                <a
                  href={`https://happy-sand-0c513521e.2.azurestaticapps.net/opinions/${opinion.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-lg font-bold transition-colors"
                  title="View opinion details"
                >
                  §
                </a>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => copyToClipboard(opinion.id, `opinion-id-${opinion.id}`)}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs"
                >
                  <Copy className="w-3 h-3" />
                  {copiedItems.has(`opinion-id-${opinion.id}`) ? 'ID Copied!' : 'Copy ID'}
                </button>
                
                {opinion.link && (
                  <a
                    href={opinion.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-xs underline"
                  >
                    View Full Opinion
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to map search results to opinions when searchedOpinions is not available
function mapSearchResultsToOpinions(searchResults: OpinionSearchResult): SearchedOpinion[] {
  if (!searchResults.hits) return [];
  
  return searchResults.hits.map(hit => ({
    id: hit.id,
    link: `https://search.cnscaseportal.com/opinions/case/${hit.id}`,
    title: hit.fields?.caseNumber || undefined,
    snippet: hit.relevantPassages[0]?.text.substring(0, 200) + '...' || undefined,
    score: hit.score,
    searchedAt: new Date().toISOString(),
    
    // Enhanced case metadata
    plaintiffs: hit.fields?.plaintiffs || [],
    defendants: hit.fields?.defendants || [],
    judge: hit.fields?.judge || '',
    court: hit.fields?.court || '',
    caseNumber: hit.fields?.caseNumber || '',
    categories: hit.fields?.categories || [],
    location: [hit.fields?.city, hit.fields?.state].filter(Boolean).join(', ') || '',
    cause: hit.fields?.cause || '',
    courthouseName: hit.fields?.courthouseName || '',
    subCourt: hit.fields?.subCourt || '',
    state: hit.fields?.state || '',
    city: hit.fields?.city || '',
    summary: hit.fields?.summary || '',
    fillingDate: hit.fields?.fillingDate || '',
    
    // AI-generated summary (directly from hit)
    aiSummary: hit.aiSummary,
    
    // Paragraph-level information with AI summaries
    paragraphs: hit.relevantPassages.map(passage => ({
      text: passage.text,
      offset: passage.offset,
      length: passage.length,
      score: passage.score,
      highlights: hit.highlights,
      aiSummary: passage.aiSummary
    })),
    
    // Multi-part query matching
    matchedQueryPartIndices: hit.matchedQueryPartIndices
  }));
}

// Helper function to render highlighted text
function renderHighlightedText(text: string, highlights: Record<string, string[]>) {
  if (!highlights || Object.keys(highlights).length === 0) {
    return text;
  }

  // For now, just return the text as-is. In a more sophisticated implementation,
  // you would parse the highlights and apply styling to matching terms.
  return text;
}